// Toasts, modal dialogs and progress indicators.
import { fmt, t } from '../i18n';
import { h } from './dom';
import { icon } from './icons';

let region: HTMLElement | null = null;

function toastRegion(): HTMLElement {
  if (!region) {
    region = h('div', { class: 'toasts', role: 'status', 'aria-live': 'polite' });
    document.body.append(region);
  }
  return region;
}

export type Tone = 'info' | 'success' | 'warn' | 'error';

const TONE_ICON: Record<Tone, string> = { info: 'info', success: 'circle-check', warn: 'warning', error: 'warning' };

export function toast(message: string, tone: Tone = 'info', action?: { label: string; run: () => void }, timeout = 6000): void {
  const close = () => el.remove();
  const el = h(
    'div',
    { class: `toast ${tone}` },
    icon(TONE_ICON[tone]),
    h('p', null, message),
    action && h('button', { class: 'btn btn-accent', type: 'button', onclick: () => (action.run(), close()) }, action.label),
    h('button', { class: 'icon-btn', type: 'button', 'aria-label': t().common.close, onclick: close }, icon('x')),
  );
  toastRegion().append(el);
  if (timeout > 0 && !action) setTimeout(close, timeout);
}

/** Opens a modal <dialog>; resolves with the value passed to close(). */
export function modal<T>(build: (close: (value: T | null) => void) => HTMLElement, opts: { wide?: boolean; label?: string } = {}): Promise<T | null> {
  return new Promise((resolve) => {
    let settled = false;
    const dialog = h('dialog', { class: `modal${opts.wide ? ' wide' : ''}`, 'aria-label': opts.label });
    const close = (value: T | null) => {
      if (settled) return;
      settled = true;
      dialog.close();
      dialog.remove();
      resolve(value);
    };
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      close(null);
    });
    dialog.append(build(close));
    document.body.append(dialog);
    dialog.showModal();
  });
}

export function confirmDialog(title: string, text: string, okLabel: string, cancelLabel = t().common.cancel): Promise<boolean> {
  return modal<boolean>((close) =>
    h(
      'div',
      { class: 'modal-body' },
      h('h2', null, title),
      h('p', null, text),
      h(
        'div',
        { class: 'modal-actions' },
        h('button', { class: 'btn btn-ghost', type: 'button', onclick: () => close(false) }, cancelLabel),
        h('button', { class: 'btn btn-primary', type: 'button', autofocus: true, onclick: () => close(true) }, okLabel),
      ),
    ),
  ).then((v) => v === true);
}

/** Asks for a PDF password. Resolves null when the user skips the file. */
export function passwordDialog(fileName: string, wrong: boolean): Promise<string | null> {
  const d = t();
  return modal<string>((close) => {
    const input = h('input', { class: 'input', type: 'password', id: 'pdf-password', autocomplete: 'off', required: true, 'aria-invalid': wrong ? 'true' : undefined });
    const show = h('input', { type: 'checkbox', onchange: () => (input.type = show.checked ? 'text' : 'password') });
    const form = h(
      'form',
      {
        class: 'modal-body',
        onsubmit: (e: SubmitEvent) => {
          e.preventDefault();
          if (input.value) close(input.value);
        },
      },
      h('h2', null, d.password.title),
      h('p', null, fmt(d.password.text, { name: fileName })),
      h('div', { class: 'field' }, h('label', { for: 'pdf-password' }, d.password.label), input, wrong && h('span', { class: 'field-error', role: 'alert' }, d.errors.wrongPassword)),
      h('label', { class: 'check' }, show, d.password.show),
      h(
        'div',
        { class: 'modal-actions' },
        h('button', { class: 'btn btn-ghost', type: 'button', onclick: () => close(null) }, d.password.skip),
        h('button', { class: 'btn btn-primary', type: 'submit' }, d.password.submit),
      ),
    );
    queueMicrotask(() => input.focus());
    return form;
  });
}

export interface ProgressView {
  el: HTMLElement;
  set(fraction: number | null, message?: string): void;
}

export function progressView(initial = t().common.processing): ProgressView {
  const fill = h('div', { class: 'progress-fill' });
  const text = h('div', { class: 'progress-text', 'aria-live': 'polite' }, initial);
  const track = h('div', { class: 'progress-track', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100' }, fill);
  const el = h('div', { class: 'progress indeterminate' }, track, text);
  return {
    el,
    set(fraction, message) {
      if (fraction === null) {
        el.classList.add('indeterminate');
        fill.style.width = '';
        track.removeAttribute('aria-valuenow');
      } else {
        el.classList.remove('indeterminate');
        const pct = Math.round(Math.max(0, Math.min(1, fraction)) * 100);
        fill.style.width = `${pct}%`;
        track.setAttribute('aria-valuenow', String(pct));
      }
      if (message !== undefined) text.textContent = message;
    },
  };
}

export function note(text: string | Node, tone: 'info' | 'warn' | 'error' | 'success' = 'info'): HTMLElement {
  return h('div', { class: `note ${tone === 'info' ? '' : tone}`.trim(), role: tone === 'error' ? 'alert' : undefined }, icon(tone === 'info' ? 'info' : tone === 'success' ? 'circle-check' : 'warning'), h('p', null, text));
}
